import { MapPin, Calendar, Clock, Ticket, ExternalLink, Timer, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * CinemaListItem - Premium horizontal card for cinema listings
 * Variant: "smart" (gold styling with discount) or "regular" (subtle styling)
 * Now supports multiple sessions - displays all date/time combinations
 */
export const CinemaListItem = ({ 
  cinema, 
  eventDate, 
  eventTime, 
  isLive, 
  index, 
  isSmart = false,
  onPurchase,
  showCountdown = false,
  timeLeft = null,
  isUrgent = false,
  isLoggedIn = false
}) => {
  const navigate = useNavigate();
  
  // Format event date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('es-ES', options);
  };

  // Get all sessions to display
  // Priority: cinema.sessions > legacy projection_date/schedule > eventDate/eventTime
  const getSessions = () => {
    // If cinema has sessions array with valid entries
    if (cinema.sessions && cinema.sessions.length > 0) {
      const validSessions = cinema.sessions.filter(s => s.date || s.time);
      if (validSessions.length > 0) {
        return validSessions.map(s => ({
          date: s.date || null,
          time: s.time || null
        }));
      }
    }
    
    // Fallback to legacy fields
    const date = cinema.projection_date || (isLive ? eventDate : null);
    const time = cinema.schedule || (isLive ? eventTime : null);
    
    if (date || time) {
      return [{ date, time }];
    }
    
    return [];
  };

  const sessions = getSessions();

  // Handle SMART button click
  const handleSmartClick = () => {
    if (isLoggedIn) {
      onPurchase && onPurchase(cinema);
    } else {
      navigate('/mi-cuenta');
    }
  };

  return (
    <div 
      className={`
        relative flex flex-col md:flex-row items-stretch md:items-center justify-between 
        w-full min-h-[80px] bg-[#121212] border overflow-hidden group cursor-pointer
        transition-all duration-300 ease-out
        ${isSmart 
          ? 'border-[var(--primary)]/20 hover:border-[var(--primary)]/50 hover:shadow-[0_0_25px_rgba(212,175,55,0.15)]' 
          : 'border-white/5 hover:border-white/20 hover:shadow-[0_0_15px_rgba(255,255,255,0.05)]'
        }
      `}
      data-testid={`cinema-item-${index}`}
    >
      {/* Lateral Gold Bar - SMART only */}
      {isSmart && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[var(--primary)] to-transparent opacity-80" />
      )}

      {/* Content Wrapper */}
      <div className={`flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6 p-4 md:p-5 flex-grow ${isSmart ? 'md:pl-6' : ''}`}>
        
        {/* Cinema Name + Badge */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <h4 className="font-['Playfair_Display'] text-lg md:text-xl text-white group-hover:text-[var(--primary)] transition-colors">
            {cinema.cinema_name}
          </h4>
          {isSmart && (
            <span className="bg-[var(--primary)] text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
              -25%
            </span>
          )}
        </div>

        {/* Info Row: City | Sessions (Date + Time) - All in one line */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/50">
          {/* City */}
          <span className="flex items-center gap-1.5">
            <MapPin size={14} className="text-[var(--primary)]" />
            <span>{cinema.city}</span>
          </span>
          
          {/* All Sessions inline */}
          {sessions.length > 0 && sessions.map((session, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {session.date && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-[var(--primary)]" />
                  <span>{formatEventDate(session.date)}</span>
                </span>
              )}
              {session.time && (
                <span className="flex items-center gap-1.5">
                  <Clock size={14} className="text-[var(--primary)]" />
                  <span>{session.time}</span>
                </span>
              )}
              {idx < sessions.length - 1 && (
                <span className="text-white/20">|</span>
              )}
            </div>
          ))}
        </div>

        {/* SMART Countdown - Compact inline version */}
        {isSmart && showCountdown && timeLeft && (
          <div className={`flex items-center gap-2 text-xs ${isUrgent ? 'text-red-400' : 'text-[var(--primary)]'}`}>
            <Timer size={14} className={isUrgent ? 'animate-pulse' : ''} />
            <span className="font-mono font-bold">
              {timeLeft.days > 0 && `${timeLeft.days}d `}
              {String(timeLeft.hours).padStart(2, '0')}:
              {String(timeLeft.minutes).padStart(2, '0')}:
              {String(timeLeft.seconds).padStart(2, '0')}
            </span>
          </div>
        )}
      </div>

      {/* Action Area - Button */}
      <div className="p-4 md:pr-5 flex-shrink-0 w-full md:w-auto">
        {isSmart && cinema.embed_code ? (
          <button
            onClick={handleSmartClick}
            className="
              w-full md:w-auto flex items-center justify-center gap-2
              bg-[var(--primary)] hover:bg-[#F2D06B] text-black font-bold
              px-6 py-3 uppercase tracking-widest text-xs
              transition-all duration-300
              shadow-[0_0_15px_rgba(212,175,55,0.25)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]
            "
            data-testid={`cinema-smart-btn-${index}`}
          >
            {isLoggedIn ? (
              <>
                <Ticket size={16} />
                <span>Comprar SMART</span>
              </>
            ) : (
              <>
                <UserPlus size={16} />
                <span>Regístrate</span>
              </>
            )}
          </button>
        ) : (cinema.website_url || cinema.ticket_url) && (
          <a
            href={cinema.website_url || cinema.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="
              w-full md:w-auto flex items-center justify-center gap-2
              bg-transparent border border-[var(--primary)]/40 text-[var(--primary)]
              hover:bg-[var(--primary)] hover:text-black hover:border-[var(--primary)]
              px-6 py-3 uppercase tracking-widest text-xs font-medium
              transition-all duration-300
            "
            data-testid={`cinema-web-btn-${index}`}
          >
            <span>Comprar Web</span>
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

export default CinemaListItem;
